import { useState, useCallback } from 'react'
import { Loader2 } from 'lucide-react'
import type { ELocalInitialData, UploadStatus, AnalyticsData } from './types'
import { fetchStats } from './api'
import { UploadPanel } from './components/UploadPanel'
import { SummaryTables } from './components/SummaryTables'
import { NotBookedBreakdown } from './components/NotBookedBreakdown'
import { KpiCards } from './components/KpiCards'
import { ConversionFunnel } from './components/ConversionFunnel'
import { PaymentFunnel } from './components/PaymentFunnel'
import { BillableBreakdown } from './components/BillableBreakdown'
import { MatchedLeadsTable } from './components/MatchedLeadsTable'

interface ELocalAnalyticsDashboardProps {
  initialData: ELocalInitialData
}

export function ELocalAnalyticsDashboard({ initialData }: ELocalAnalyticsDashboardProps) {
  const [periodMonth, setPeriodMonth] = useState(initialData.period_month)
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>(initialData.upload_status)
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(initialData.analytics)
  const [loading, setLoading] = useState(false)
  const [availableMonths] = useState(initialData.available_months)

  const allUploaded = uploadStatus.elocal_leads && uploadStatus.workiz_jobs && uploadStatus.workiz_payments

  const refreshAnalytics = useCallback(async (month: string) => {
    setLoading(true)
    try {
      const data = await fetchStats(month)
      setAnalytics(data)
    } catch {
      // keep existing
    } finally {
      setLoading(false)
    }
  }, [])

  const handleUploadComplete = useCallback(
    (newStatus: UploadStatus) => {
      setUploadStatus(newStatus)
      // If all 3 are uploaded, refresh analytics
      if (newStatus.elocal_leads && newStatus.workiz_jobs && newStatus.workiz_payments) {
        refreshAnalytics(periodMonth)
      }
    },
    [periodMonth, refreshAnalytics]
  )

  const handleMonthChange = useCallback(
    (month: string) => {
      setPeriodMonth(month)
      // Reload via page navigation to get fresh data
      window.location.href = `/dashboard/elocal-analytics?period_month=${month}`
    },
    []
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">eLocal Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">Lead purchasing effectiveness analysis</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-600">Period:</label>
          {availableMonths.length > 0 ? (
            <select
              value={periodMonth}
              onChange={(e) => handleMonthChange(e.target.value)}
              className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              {availableMonths.map((m) => (
                <option key={m} value={m}>
                  {new Date(m + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-sm text-gray-400">No data uploaded yet</span>
          )}
          <input
            type="month"
            value={periodMonth}
            onChange={(e) => handleMonthChange(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            title="Select a different month"
          />
        </div>
      </div>

      {/* Upload Panel */}
      <UploadPanel
        periodMonth={periodMonth}
        uploadStatus={uploadStatus}
        onUploadComplete={handleUploadComplete}
      />

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          <span className="ml-3 text-gray-500">Calculating analytics...</span>
        </div>
      )}

      {/* Analytics - only show if all 3 files uploaded */}
      {!loading && analytics && allUploaded && (
        <>
          {/* Summary Tables */}
          <SummaryTables data={analytics.summary} />

          {/* Billable Not Booked */}
          <NotBookedBreakdown data={analytics.not_booked} />

          {/* Charts Row */}
          <div className="grid grid-cols-3 gap-6">
            <ConversionFunnel funnel={analytics.funnel} />
            <PaymentFunnel data={analytics.payment_funnel} />
            <BillableBreakdown data={analytics.billable_breakdown} />
          </div>

          {/* Detail Table */}
          <MatchedLeadsTable
            initialLeads={analytics.leads_detail.slice(0, 25)}
            periodMonth={periodMonth}
            totalLeads={analytics.leads_detail.length}
          />
        </>
      )}

      {/* Empty state */}
      {!loading && !allUploaded && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
          <p className="text-gray-500 text-lg">Upload all three CSV files to see analytics</p>
          <p className="text-gray-400 text-sm mt-2">
            eLocal Leads + Workiz Jobs + Workiz Payments
          </p>
        </div>
      )}
    </div>
  )
}
